/**
 * File Compression Utilities
 *
 * Provides gzip compression for CSV and JSON files before upload.
 * Uses the native CompressionStream API (available in all modern browsers).
 *
 * Only CSV and JSON files are compressed — they typically compress 5-10x.
 * XLSX/XLS (already ZIP archives) and Parquet (already ZSTD compressed) are skipped.
 *
 * @module lib/compression
 */

import { getFileExtension as getExtension } from '@loader/shared-types';

/** File types that benefit from gzip compression */
const COMPRESSIBLE_EXTENSIONS = new Set(['csv', 'json']);

/**
 * Check if a file should be compressed before upload
 */
export function isCompressible(fileName: string): boolean {
  if (typeof CompressionStream === 'undefined') return false;
  const ext = getExtension(fileName);
  return COMPRESSIBLE_EXTENSIONS.has(ext);
}

/**
 * Result of compression attempt
 */
export interface CompressionResult {
  /** The (possibly compressed) blob to upload */
  blob: Blob;
  /** The filename to use in FormData (may have .gz suffix) */
  fileName: string;
  /** Whether compression was applied */
  compressed: boolean;
}

/**
 * Compress a file with gzip if it's a compressible type (CSV/JSON)
 *
 * Falls back to the original file if:
 * - The file type is not compressible (XLSX, Parquet, etc.)
 * - CompressionStream is not available
 * - Compression fails for any reason
 *
 * @param file - The file to potentially compress
 * @returns The compression result with blob, filename, and whether compression was applied
 */
export async function compressFile(file: File): Promise<CompressionResult> {
  if (!isCompressible(file.name)) {
    return { blob: file, fileName: file.name, compressed: false };
  }

  try {
    const stream = file.stream().pipeThrough(new CompressionStream('gzip'));
    const compressedBlob = await new Response(stream).blob();

    return {
      blob: compressedBlob,
      fileName: `${file.name}.gz`,
      compressed: true,
    };
  } catch {
    // Fallback to uncompressed on any error
    return { blob: file, fileName: file.name, compressed: false };
  }
}
